import { useState, useEffect } from "react";
import { useAuth, API } from "@/App";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import axios from "axios";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Textarea } from "@/components/ui/textarea";
import {
  Star, Utensils, Wine, CakeSlice, Leaf, User, LogOut, Settings, Menu, X, Save
} from "lucide-react";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { FavoriteItemsCard } from "@/components/preferences/FavoriteItemsCard";
import { CuisineCard, BudgetCard, SauceCard } from "@/components/preferences/PreferenceCards";
import {
  DISH_SUGGESTIONS, DRINK_SUGGESTIONS, DESSERT_SUGGESTIONS, DIETARY_RESTRICTIONS
} from "@/components/preferences/constants";

const Preferences = () => {
  const { user, logout } = useAuth();
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [preferences, setPreferences] = useState({
    cuisines: [],
    favorite_dishes: [],
    favorite_drinks: [],
    favorite_desserts: [],
    dietary_restrictions: [],
    sauce_preferences: [],
    custom_sauces: [],
    budget_level: null,
    budget_max: "",
    notes: ""
  });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  useEffect(() => {
    const fetchPreferences = async () => {
      try {
        const response = await axios.get(`${API}/users/preferences`, { withCredentials: true });
        setPreferences(response.data);
      } catch (error) {
        console.warn("Error fetching preferences:", error);
      } finally {
        setLoading(false);
      }
    };
    fetchPreferences();
  }, []);

  const handleSave = async () => {
    setSaving(true);
    try {
      await axios.put(`${API}/users/preferences`, preferences, { withCredentials: true });
      toast.success("Preferences saved successfully!");
    } catch (error) {
      toast.error("Failed to save preferences");
    } finally {
      setSaving(false);
    }
  };

  const setField = (field) => (value) => setPreferences((prev) => ({ ...prev, [field]: value }));

  const toggleCuisine = (cuisineId) => {
    setPreferences((prev) => ({
      ...prev,
      cuisines: prev.cuisines.includes(cuisineId)
        ? prev.cuisines.filter((c) => c !== cuisineId)
        : [...prev.cuisines, cuisineId]
    }));
  };

  const toggleSauce = (sauce) => {
    setPreferences((prev) => ({
      ...prev,
      sauce_preferences: prev.sauce_preferences.includes(sauce)
        ? prev.sauce_preferences.filter((s) => s !== sauce)
        : [...prev.sauce_preferences, sauce]
    }));
  };

  const handleLogout = async () => {
    await logout();
    navigate('/');
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="w-8 h-8 border-2 border-primary border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background" data-testid="preferences-page">
      <nav className="border-b border-border bg-card/50 backdrop-blur-sm sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-16">
            <div className="flex items-center gap-2 cursor-pointer" onClick={() => navigate('/dashboard')}>
              <Star className="w-5 h-5 text-primary fill-primary" />
              <span className="font-semibold gradient-text">Étoile</span>
            </div>

            <div className="hidden md:flex items-center gap-8">
              <button onClick={() => navigate('/dashboard')} className="menu-item text-sm text-muted-foreground hover:text-foreground py-2">{t('common.dashboard')}</button>
              <button onClick={() => navigate('/restaurants')} className="menu-item text-sm text-muted-foreground hover:text-foreground py-2">{t('common.restaurants')}</button>
              <button onClick={() => navigate('/preferences')} className="menu-item text-sm text-foreground py-2">{t('common.preferences')}</button>
              <button onClick={() => navigate('/leaderboard')} className="menu-item text-sm text-muted-foreground hover:text-foreground py-2">{t('common.leaderboard')}</button>
            </div>

            <div className="hidden md:flex items-center gap-4">
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button variant="ghost" className="flex items-center gap-2 h-10">
                    {user?.picture ? (
                      <img src={user.picture} alt="" className="w-8 h-8 rounded-full" />
                    ) : (
                      <div className="w-8 h-8 rounded-full bg-primary/20 flex items-center justify-center">
                        <User className="w-4 h-4 text-primary" />
                      </div>
                    )}
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end" className="w-48 bg-popover border-border">
                  <DropdownMenuItem onClick={() => navigate('/profile')} className="cursor-pointer">
                    <Settings className="w-4 h-4 mr-2" />Profile Settings
                  </DropdownMenuItem>
                  <DropdownMenuSeparator className="bg-muted/50" />
                  <DropdownMenuItem onClick={handleLogout} className="cursor-pointer text-destructive">
                    <LogOut className="w-4 h-4 mr-2" />Logout
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            </div>

            <button className="md:hidden p-2" onClick={() => setMobileMenuOpen(!mobileMenuOpen)}>
              {mobileMenuOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
            </button>
          </div>

          {mobileMenuOpen && (
            <div className="md:hidden py-4 border-t border-border">
              <div className="flex flex-col gap-2">
                <button onClick={() => { navigate('/dashboard'); setMobileMenuOpen(false); }} className="text-left py-2 text-muted-foreground">{t('common.dashboard')}</button>
                <button onClick={() => { navigate('/restaurants'); setMobileMenuOpen(false); }} className="text-left py-2 text-muted-foreground">{t('common.restaurants')}</button>
                <button onClick={() => { navigate('/preferences'); setMobileMenuOpen(false); }} className="text-left py-2 text-foreground">{t('common.preferences')}</button>
                <button onClick={() => { navigate('/leaderboard'); setMobileMenuOpen(false); }} className="text-left py-2 text-muted-foreground">{t('common.leaderboard')}</button>
                <button onClick={() => { navigate('/profile'); setMobileMenuOpen(false); }} className="text-left py-2 text-muted-foreground">Profile</button>
                <button onClick={handleLogout} className="text-left py-2 text-destructive">Logout</button>
              </div>
            </div>
          )}
        </div>
      </nav>

      <main className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="flex justify-between items-center mb-8">
          <div>
            <h1 className="text-3xl font-bold tracking-tight mb-2">{t('common.preferences')}</h1>
            <p className="text-muted-foreground">Tell us about your culinary tastes</p>
          </div>
          <Button
            onClick={handleSave}
            disabled={saving}
            className="bg-primary text-primary-foreground hover:bg-primary/90"
            data-testid="save-preferences-button"
          >
            <Save className="w-4 h-4 mr-2" />
            {saving ? "Saving..." : "Save Preferences"}
          </Button>
        </div>

        <div className="space-y-8">
          <CuisineCard selected={preferences.cuisines} onToggle={toggleCuisine} />

          <BudgetCard
            budgetLevel={preferences.budget_level}
            budgetMax={preferences.budget_max}
            onLevelChange={setField("budget_level")}
            onMaxChange={setField("budget_max")}
          />

          <SauceCard
            selected={preferences.sauce_preferences}
            customSauces={preferences.custom_sauces}
            onToggle={toggleSauce}
            onCustomChange={setField("custom_sauces")}
          />

          <FavoriteItemsCard
            title="Favorite Dishes"
            icon={Utensils}
            description="Select your favorites or add your own"
            suggestions={DISH_SUGGESTIONS}
            values={preferences.favorite_dishes}
            onChange={setField("favorite_dishes")}
            placeholder="Add custom dish..."
            cardTestId="favorite-dishes-card"
            inputTestId="new-dish-input"
            buttonTestId="add-dish-button"
          />

          <FavoriteItemsCard
            title="Favorite Desserts"
            icon={CakeSlice}
            description="Desserts are our specialty! Select your favorites"
            suggestions={DESSERT_SUGGESTIONS}
            values={preferences.favorite_desserts}
            onChange={setField("favorite_desserts")}
            placeholder="Add custom dessert..."
            cardTestId="favorite-desserts-card"
            inputTestId="new-dessert-input"
            buttonTestId="add-dessert-button"
            featured
            badgeLabel="Featured"
          />

          <FavoriteItemsCard
            title="Favorite Drinks"
            icon={Wine}
            description="Select your favorites or add your own"
            suggestions={DRINK_SUGGESTIONS}
            values={preferences.favorite_drinks}
            onChange={setField("favorite_drinks")}
            placeholder="Add custom drink..."
            cardTestId="favorite-drinks-card"
            inputTestId="new-drink-input"
            buttonTestId="add-drink-button"
          />

          <FavoriteItemsCard
            title="Dietary Restrictions"
            icon={Leaf}
            description="Select any that apply or add your own"
            suggestions={DIETARY_RESTRICTIONS}
            values={preferences.dietary_restrictions}
            onChange={setField("dietary_restrictions")}
            placeholder="Add custom restriction..."
            cardTestId="dietary-restrictions-card"
            inputTestId="new-restriction-input"
            buttonTestId="add-restriction-button"
            destructive
          />

          <Card className="bg-card border-border" data-testid="notes-card">
            <CardHeader>
              <CardTitle className="text-lg">Additional Notes</CardTitle>
            </CardHeader>
            <CardContent>
              <Textarea
                placeholder="Any other preferences or notes for restaurants..."
                value={preferences.notes || ""}
                onChange={(e) => setPreferences({ ...preferences, notes: e.target.value })}
                className="bg-transparent border-border focus:border-primary min-h-[100px]"
                data-testid="notes-textarea"
              />
            </CardContent>
          </Card>
        </div>
      </main>
    </div>
  );
};

export default Preferences;
